import {
  Button,
  dayRange,
  formatDateTime,
  formatPhone,
  PageEmpty,
  PageError,
  PageLoading,
  TextButton,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { DateRangePicker } from "@majitap/ui/arc/date-range-picker/date-range-picker";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { LineChart } from "@majitap/ui/arc/line-chart/line-chart";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { isCoordinate, toCoordinate } from "../../coordinates";
import {
  type Location,
  type Owner,
  type PlaceFields,
  placeActions,
  placesQuery,
  READINGS_CAP,
  readingsQuery,
  sensorQuery,
  type Tank,
  tanksQuery,
} from "../../customers";
import { messageFor } from "../../error-copy";
import { AddressField } from "../shared/address-field";
import { asText, PointField } from "../shared/point-field";
import styles from "./customers.module.css";

/**
 * A customer's or business's places and their tanks (`Pmflg` in the design). Each place says
 * which service zone it falls in: a place outside every zone can't be priced or ordered to.
 */
export function PlacesPanel({ owner, canEdit }: { owner: Owner; canEdit: boolean }): ReactNode {
  const places = useQuery(placesQuery(owner));
  const [dialog, setDialog] = useState<
    | { kind: "place"; place?: Location }
    | { kind: "remove"; place: Location }
    | { kind: "tank"; place: Location; tank?: Tank }
    | { kind: "readings"; tank: Tank }
    | null
  >(null);
  if (places.status === "pending") return <PageLoading label="Loading places" />;
  if (places.status === "error")
    return <PageError message={messageFor(errorOf(places.error))} onRetry={() => places.refetch()} />;
  const close = () => setDialog(null);

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <span className={styles.muted}>{places.data.length === 1 ? "1 place" : `${places.data.length} places`}</span>
        {canEdit ? <TextButton onClick={() => setDialog({ kind: "place" })}>Add a place</TextButton> : null}
      </div>
      {places.data.length === 0 ? (
        <PageEmpty title="No places yet" description="Add the place water goes to before placing an order." />
      ) : (
        <div className={styles.grid}>
          {places.data.map((place) => (
            <article key={place.id} className={styles.card}>
              <header className={styles.cardHead}>
                <span className={styles.stack}>
                  <span className={styles.title}>
                    {place.label} {place.is_default ? <Badge tone="neutral">Default</Badge> : null}
                  </span>
                  <span className={styles.muted}>
                    {[place.address_line, place.floor ? `floor ${place.floor}` : null, place.landmark]
                      .filter(Boolean)
                      .join(", ") || "No address"}
                  </span>
                  {place.contact_name || place.contact_phone ? (
                    <span className={styles.muted}>
                      On site: {[place.contact_name, formatPhone(place.contact_phone)].filter(Boolean).join(", ")}
                    </span>
                  ) : null}
                </span>
                {place.geofence ? (
                  <Badge tone="info">{place.geofence.name}</Badge>
                ) : (
                  <Badge tone="warning">Outside service zones</Badge>
                )}
              </header>
              <Tanks
                place={place}
                canEdit={canEdit}
                onEdit={(tank) => setDialog({ kind: "tank", place, tank })}
                onReadings={(tank) => setDialog({ kind: "readings", tank })}
              />
              {canEdit ? (
                <footer className={styles.actions}>
                  <TextButton onClick={() => setDialog({ kind: "place", place })}>Edit</TextButton>
                  <TextButton onClick={() => setDialog({ kind: "tank", place })}>Add a tank</TextButton>
                  {place.is_default ? null : <MakeDefault placeId={place.id} />}
                  <TextButton className={styles.danger} onClick={() => setDialog({ kind: "remove", place })}>
                    Remove
                  </TextButton>
                </footer>
              ) : null}
            </article>
          ))}
        </div>
      )}
      {dialog?.kind === "place" ? <PlaceDialog owner={owner} place={dialog.place} onClose={close} /> : null}
      {dialog?.kind === "remove" ? <RemovePlaceDialog place={dialog.place} onClose={close} /> : null}
      {dialog?.kind === "tank" ? <TankDialog place={dialog.place} tank={dialog.tank} onClose={close} /> : null}
      {dialog?.kind === "readings" ? (
        <ReadingsDialog sensorId={dialog.tank.sensor_id} title={dialog.tank.label ?? "Tank"} onClose={close} />
      ) : null}
    </div>
  );
}

function Tanks({
  place,
  canEdit,
  onEdit,
  onReadings,
}: {
  place: Location;
  canEdit: boolean;
  onEdit: (tank: Tank) => void;
  onReadings: (tank: Tank) => void;
}) {
  const tanks = useQuery(tanksQuery(place.id));
  if (!tanks.data?.length)
    return <p className={styles.muted}>{tanks.isPending ? "Loading tanks…" : "No tanks recorded."}</p>;
  return (
    <ul className={styles.tanks}>
      {tanks.data.map((tank) => (
        <TankRow
          key={tank.id}
          tank={tank}
          canEdit={canEdit}
          onEdit={() => onEdit(tank)}
          onReadings={() => onReadings(tank)}
        />
      ))}
    </ul>
  );
}

/** One tank with its level from the sensor, red at or below the tank's low level. */
function TankRow({
  tank,
  canEdit,
  onEdit,
  onReadings,
}: {
  tank: Tank;
  canEdit: boolean;
  onEdit: () => void;
  onReadings: () => void;
}) {
  const format = useCompanyFormat();
  const sensor = useQuery({ ...sensorQuery(tank.sensor_id ?? ""), enabled: tank.sensor_id !== null });
  const reading = sensor.data?.latest;
  // number-ok: a tank level for the bar's width and the low check, not money
  const level = reading ? Number(reading.level_percent) : null;
  // number-ok: the tank's low threshold, a percentage
  const low = level !== null && level <= Number(tank.low_level_percent);
  return (
    <li className={styles.tank}>
      <div className={styles.tankTop}>
        <span>
          {[tank.label ?? "Tank", tank.capacity_litres ? `${tank.capacity_litres.toLocaleString("en-KE")} L` : null]
            .filter(Boolean)
            .join(" · ")}
          {tank.is_active ? null : <span className={styles.muted}> · Retired</span>}
        </span>
        <span className={low ? styles.low : styles.level}>
          {level === null ? (tank.sensor_id ? "No reading" : "No sensor") : `${reading?.level_percent}%`}
        </span>
      </div>
      <div className={styles.bar} aria-hidden="true">
        {level !== null ? (
          <span className={low ? styles.barLow : styles.barFill} style={{ width: `${Math.min(level, 100)}%` }} />
        ) : null}
      </div>
      <div className={styles.tankFoot}>
        <span className={styles.muted}>
          {reading
            ? `Read ${formatDateTime(reading.measured_at, format)} · low at ${tank.low_level_percent}%`
            : `Low at ${tank.low_level_percent}%`}
        </span>
        <span className={styles.actions}>
          {tank.sensor_id ? <TextButton onClick={onReadings}>Readings</TextButton> : null}
          {canEdit ? <TextButton onClick={onEdit}>Edit</TextButton> : null}
        </span>
      </div>
    </li>
  );
}

function useRefreshPlaces() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["places"] });
    await queryClient.invalidateQueries({ queryKey: ["tanks"] });
  };
}

const optional = (value: string) => value.trim() || undefined;

/**
 * Add or edit a place. Search the address (Google Places, P8-41) to fill it and pin it, or set the
 * point on the map or by typing; the point decides its service zone.
 */
export function PlaceDialog({ owner, place, onClose }: { owner: Owner; place?: Location; onClose: () => void }) {
  const refresh = useRefreshPlaces();
  const [label, setLabel] = useState(place?.label ?? "");
  const [address, setAddress] = useState(place?.address_line ?? "");
  const [landmark, setLandmark] = useState(place?.landmark ?? "");
  const [floor, setFloor] = useState(place?.floor?.toString() ?? "");
  const [contact, setContact] = useState(place?.contact_name ?? "");
  const [contactPhone, setContactPhone] = useState(place?.contact_phone ?? "");
  const [lat, setLat] = useState(place?.latitude?.toString() ?? "");
  const [lng, setLng] = useState(place?.longitude?.toString() ?? "");
  const valid =
    label.trim() && isCoordinate(lat, 90) && isCoordinate(lng, 180) && (floor === "" || /^\d{1,3}$/.test(floor));
  const mutation = useMutation({
    mutationFn: () => {
      const body: PlaceFields = {
        label: label.trim(),
        address_line: optional(address),
        landmark: optional(landmark),
        // number-ok: a floor number, not money
        floor: floor ? Number.parseInt(floor, 10) : undefined,
        contact_name: optional(contact),
        contact_phone: optional(contactPhone),
        latitude: toCoordinate(lat),
        longitude: toCoordinate(lng),
      };
      return place ? placeActions.update(place.id, body) : placeActions.create(owner, body);
    },
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={place ? `Edit ${place.label}` : "Add a place"}
        description="Search the address to pin it, or click the map. The pin decides which service zone the place is in."
      >
        <div className={styles.form}>
          <Input
            label="Name"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            description="How the customer calls it, e.g. Home or Main kitchen."
          />
          <AddressField
            label="Address (optional)"
            value={address}
            onChange={setAddress}
            onPick={(picked) => {
              setAddress(picked.address);
              setLat(asText(picked.lat));
              setLng(asText(picked.lng));
            }}
          />
          <div className={styles.pair}>
            <Input label="Landmark (optional)" value={landmark} onChange={(e) => setLandmark(e.target.value)} />
            <Input
              label="Floor (optional)"
              inputMode="numeric"
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
            />
          </div>
          <PointField
            lat={lat}
            lng={lng}
            onChange={(la, ln) => {
              setLat(la);
              setLng(ln);
            }}
          />
          <div className={styles.pair}>
            <Input label="Contact on site (optional)" value={contact} onChange={(e) => setContact(e.target.value)} />
            <Input
              label="Their phone (optional)"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+254712345678"
            />
          </div>
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!valid} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              {place ? "Save" : "Add place"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RemovePlaceDialog({ place, onClose }: { place: Location; onClose: () => void }) {
  const refresh = useRefreshPlaces();
  const mutation = useMutation({
    mutationFn: () => placeActions.remove(place.id),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Remove ${place.label}?`}
        description="Orders already placed to it keep their place. New orders can't go there."
      >
        <div className={styles.form}>
          {mutation.error ? (
            <Alert tone="danger" title="Not removed">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Keep it
            </Button>
            <Button variant="danger" loading={mutation.isPending} onClick={() => mutation.mutate()}>
              Remove
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Add or edit a tank: its name, size and the level at which it counts as low (at most 95%). */
function TankDialog({ place, tank, onClose }: { place: Location; tank?: Tank; onClose: () => void }) {
  const refresh = useRefreshPlaces();
  const [label, setLabel] = useState(tank?.label ?? "");
  const [capacity, setCapacity] = useState(tank?.capacity_litres?.toString() ?? "");
  const [low, setLow] = useState(tank?.low_level_percent ?? "20");
  const valid = (capacity === "" || /^\d{1,7}$/.test(capacity)) && /^\d{1,2}(\.\d{1,2})?$/.test(low);
  const mutation = useMutation({
    mutationFn: () => {
      // number-ok: litres, not money
      const litres = capacity ? Number.parseInt(capacity, 10) : undefined;
      return tank
        ? // a field emptied on an edit is removed (null); undefined would keep the old value
          placeActions.updateTank(place.id, tank.id, {
            label: label.trim() || null,
            capacity_litres: litres ?? null,
            low_level_percent: low,
          })
        : placeActions.addTank(place.id, {
            label: label.trim() || undefined,
            capacity_litres: litres,
            low_level_percent: low,
          });
    },
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  // a retired tank stays in the history (its readings, its orders) but isn't offered any more
  const retire = useMutation({
    mutationFn: () => placeActions.updateTank(place.id, tank?.id ?? "", { is_active: !tank?.is_active }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={tank ? `Edit ${tank.label ?? "tank"}` : `Add a tank at ${place.label}`}>
        <div className={styles.form}>
          <Input
            label="Name (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Rooftop tank"
          />
          <div className={styles.pair}>
            <Input
              label="Size in litres (optional)"
              inputMode="numeric"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
            <Input
              label="Low at (%)"
              inputMode="decimal"
              value={low}
              onChange={(e) => setLow(e.target.value)}
              description="The customer and ops are alerted below it. At most 95."
            />
          </div>
          {mutation.error || retire.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error ?? retire.error))}
            </Alert>
          ) : null}
          {tank?.is_active && tank.sensor_id ? (
            <p className={styles.muted}>To retire this tank, take its sensor off first (Tank sensors › Edit).</p>
          ) : null}
          <div className={styles.footer}>
            {tank ? (
              <Button
                variant="secondary"
                disabled={tank.is_active && tank.sensor_id !== null}
                loading={retire.isPending}
                onClick={() => retire.mutate()}
              >
                {tank.is_active ? "Retire the tank" : "Bring it back"}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!valid} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              {tank ? "Save" : "Add tank"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** A tank's level over a period (the last seven days to start), from its sensor; also on Tank sensors. */
export function ReadingsDialog({
  sensorId,
  title,
  onClose,
}: {
  sensorId: string | null;
  title: string;
  onClose: () => void;
}) {
  const format = useCompanyFormat();
  // readings are kept 180 days; the chart shows a period's latest 1,000
  const [range, setRange] = useState(() => {
    const end = new Date(new Date().setHours(0, 0, 0, 0));
    return { start: new Date(end.getFullYear(), end.getMonth(), end.getDate() - 6), end };
  });
  const period = dayRange(range, format.timezone);
  const readings = useQuery({
    ...readingsQuery(sensorId ?? "", period.from, period.to),
    enabled: sensorId !== null,
  });
  const data = (readings.data ?? [])
    .filter((r): r is NonNullable<typeof r> => r !== null)
    .sort((a, b) => a.measured_at.localeCompare(b.measured_at))
    .map((r) => ({
      key: r.measured_at,
      label: formatDateTime(r.measured_at, format),
      // number-ok: a tank level for the chart
      values: { level: Number(r.level_percent) },
    }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={`${title}: its level`}>
        <DateRangePicker label="Period" value={range} onChange={setRange} maxDate={new Date()} />
        {readings.data?.length === READINGS_CAP ? (
          <p className={styles.muted}>The latest 1,000 readings of the period. Pick a shorter one to see it all.</p>
        ) : null}
        <LineChart
          label="Tank level"
          unit="%"
          data={data}
          series={[{ key: "level", label: "Level" }]}
          loading={readings.isPending}
          emptyLabel="No readings in this period"
          formatValue={(v) => `${Math.round(v)}`}
          categoryLabel="Read at"
        />
      </DialogContent>
    </Dialog>
  );
}

/** The owner's default place: where an order goes unless they pick another. One per owner. */
function MakeDefault({ placeId }: { placeId: string }) {
  const refresh = useRefreshPlaces();
  const make = useMutation({ mutationFn: () => placeActions.makeDefault(placeId), onSuccess: refresh });
  return (
    <TextButton disabled={make.isPending} onClick={() => make.mutate()}>
      Make it the default
    </TextButton>
  );
}
